import Table, { SortOrder } from 'antd/lib/table'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { PerAvatarPlus } from '../../../components/custom_components/AvatarPlus'
import { PerEmpty } from '../../../components/custom_components/Empty'
import { MeetingTag } from '../../../components/custom_components/MeetingTag'
import PerText from '../../../components/custom_components/Text'
import { useAuth } from '../../../context/auth-context'
import { useDateTime } from '../../../shared/useDateTime'
import { MeetingRequestDto, MeetingRequestStatus } from '../../../types'
import { NameDisplayPreference } from '../../../graphql/enum/name-display-preference.enum'
import { computeFullname } from '../../../shared/nameDisplayPreferencesUtil'
import { Tooltip } from 'antd'
import PerFlexRow from '../../../components/custom_components/FlexRow'
import { PerTag } from '../../../components/custom_components/Tag'

interface CompanySettingsRequestedMeetingsTableProps {
  requestedMeetings: MeetingRequestDto[]
  meetingRequestsFilter: MeetingRequestStatus
}
export const CompanySettingsRequestedMeetingsTable: FC<CompanySettingsRequestedMeetingsTableProps> = ({
  requestedMeetings,
  meetingRequestsFilter,
}) => {
  const { t } = useTranslation()
  const { user } = useAuth()
  const { compareDates, formatDateShort } = useDateTime(user)

  const employeeTableHeader = (
    <Tooltip title={t('employeeList.table.employeeTooltip')}>{t('companySettingsView.requestedMeetingsTab.table.employee')}</Tooltip>
  )

  const emptyMessage = meetingRequestsFilter
    ? t('companySettingsView.requestedMeetingsTab.table.filteredEmpty', {
        meetingRequestStatus: meetingRequestsFilter,
      })
    : t('companySettingsView.requestedMeetingsTab.table.empty')

  const columns = [
    {
      key: 'requestedOn',
      title: t('companySettingsView.requestedMeetingsTab.table.requestedOn'),
      className: 'minWidth-column-m',
      defaultSortOrder: 'ascend' as SortOrder,
      sorter: (meetingA: MeetingRequestDto, meetingB: MeetingRequestDto) => {
        return compareDates(meetingA.requestedDate, meetingB.requestedDate)
      },
      render: (meetingRequest: MeetingRequestDto) => <PerText>{formatDateShort(meetingRequest.requestedDate)}</PerText>,
    },
    {
      key: 'type',
      title: t('companySettingsView.requestedMeetingsTab.table.type'),
      className: 'minWidth-column-m',
      render: (meetingRequest: MeetingRequestDto) => <MeetingTag type={meetingRequest.type} />,
    },
    {
      key: 'reviewer',
      className: 'minWidth-column-xl',
      title: t('companySettingsView.requestedMeetingsTab.table.reviewer'),
      sorter: (rowDataA: MeetingRequestDto, rowDataB: MeetingRequestDto) => {
        const nameA = computeFullname(user.nameDisplayPreference, rowDataA.reviewer.firstName, rowDataA.reviewer.lastName)
        const nameB = computeFullname(user.nameDisplayPreference, rowDataB.reviewer.firstName, rowDataB.reviewer.lastName)
        return nameA.localeCompare(nameB)
      },
      render: (meetingRequest: MeetingRequestDto) => (
        <PerAvatarPlus
          nameDisplayPreference={user.nameDisplayPreference as NameDisplayPreference}
          employeeId={meetingRequest.reviewer.id}
          firstName={meetingRequest.reviewer.firstName}
          lastName={meetingRequest.reviewer.lastName}
          infoText={''}
          data-e2e-meeting-table-avatar
        />
      ),
    },
    {
      key: 'employee',
      className: 'minWidth-column-xl',
      title: employeeTableHeader,
      sorter: (rowDataA: MeetingRequestDto, rowDataB: MeetingRequestDto) => {
        const nameA = computeFullname(user.nameDisplayPreference, rowDataA.requester.firstName, rowDataA.requester.lastName)
        const nameB = computeFullname(user.nameDisplayPreference, rowDataB.requester.firstName, rowDataB.requester.lastName)
        return nameA.localeCompare(nameB)
      },
      render: (meetingRequest: MeetingRequestDto) => (
        <PerAvatarPlus
          nameDisplayPreference={user.nameDisplayPreference as NameDisplayPreference}
          employeeId={meetingRequest.requester.id}
          firstName={meetingRequest.requester.firstName}
          lastName={meetingRequest.requester.lastName}
          infoText={''}
          data-e2e-meeting-table-avatar
        />
      ),
    },
    {
      key: 'status',
      title: t('companySettingsView.requestedMeetingsTab.table.reason'),
      className: 'minWidth-column-xl',
      render: (meetingRequest: MeetingRequestDto) => (
        <>
          <PerFlexRow align="middle">
            <PerText tooltip={meetingRequest.reasonForRequest}>
              <PerTag color={'blue'} icon="download_fill">
                {t('companySettingsView.requestedMeetingsTab.table.reasonOfRequest')}
              </PerTag>
            </PerText>

            {meetingRequest.status === MeetingRequestStatus.Declined && (
              <>
                <PerText tooltip={meetingRequest.reasonOfRejection ? meetingRequest.reasonOfRejection : '-'}>
                  <PerTag color={'red'} icon="close_stroke">
                    {t('companySettingsView.requestedMeetingsTab.table.reasonOfRejection')}
                  </PerTag>
                </PerText>
              </>
            )}
          </PerFlexRow>
        </>
      ),
    },
  ]

  return (
    <Table
      className="light small viewWithTabsAndInfoBox scroll-compensation"
      rowClassName={() => 'minHeight-row'}
      dataSource={requestedMeetings.map((o) => ({ ...o, key: o.id }))}
      columns={columns}
      pagination={false}
      scroll={{ y: true }}
      locale={{
        emptyText: requestedMeetings.length === 0 && <PerEmpty iconSize={'xxl'} icon="chatNo_stroke" heading={emptyMessage} />,
      }}
      data-e2e-manager-meetings-table
    />
  )
}
